import { Injectable } from '@angular/core';
import { AccountActions, EnvironmentActions } from '@app/core/actions';
import * as fromRoot from '@app/reducers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { defer, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as fromSignin from '../reducers';
import { MfaActions } from '../actions';
import { Iam, errors as IamSdkErrors } from '@smash-sdk/iam/ejs/07-2021';
import { genericRetry } from '@app/shared/operators/genericRetry';
import { Constant } from 'src/constant';
import { getLanguageWithFallback } from '@app/shared/helpers/language';
import { FactorWithOtpSendableType } from '@app/shared/models/mfa';
import ListPublicServicesError from '@smash-sdk/discovery/ejs/02-2023/types/ListPublicServices/ListPublicServicesError';
import { CookiesService } from '@app/core/services/cookies.service';
import { TokenService } from '@app/core/services/token.service';
import { Region, config } from '@smash-sdk/core';
import { Discovery } from '@smash-sdk/discovery/ejs/02-2023/discovery';
import { SigninActions } from '../actions';
import { Router } from '@angular/router';


@Injectable()
export class MfaEffects {
    sendOtp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MfaActions.SendOtp),
            withLatestFrom(
                this.store$.select(fromSignin.getMfaToken),
                this.store$.select(fromSignin.getMfaRegion),
            ),
            switchMap(([action, token, region]) => {
                const { factor } = action;
                const iamSdk = new Iam({ region });
                return defer(() => iamSdk.sendOtp({ token, factor: factor as FactorWithOtpSendableType })).pipe(
                    map(() => MfaActions.SendOtpSuccess()),
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: IamSdkErrors.SendOtpError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: IamSdkErrors.SendOtpError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                            { error: IamSdkErrors.SendOtpError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    catchError(error => {
                        if (error instanceof IamSdkErrors.SendOtpError.MfaFactorNotAllowedError) {
                            return of(MfaActions.SendOtpFailure({ error: error.name }));
                        } else {
                            return of(MfaActions.SendOtpFailure({ error: error.message }));
                        }
                    })
                );
            })
        )
    );

    verifyOtp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MfaActions.VerifyOtp),
            withLatestFrom(
                this.store$.select(fromSignin.getMfaToken),
                this.store$.select(fromSignin.getMfaRegion),
            ),
            switchMap(([action, token, region]) => {
                const { factor, otp } = action;
                const iamSdk = new Iam({ region });
                return defer(() => iamSdk.verifyOtp({ token, factor, otp })).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: IamSdkErrors.VerifyMfaFactorError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: IamSdkErrors.VerifyMfaFactorError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                            { error: IamSdkErrors.VerifyMfaFactorError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                            { error: IamSdkErrors.VerifyUserMfaFactorError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: IamSdkErrors.VerifyUserMfaFactorError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                            { error: IamSdkErrors.VerifyUserMfaFactorError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    exhaustMap(({ identity }) => {
                        return of(MfaActions.VerifyOtpSuccess({ identity, region }));
                    }),
                    catchError(error => {
                        if (error instanceof IamSdkErrors.VerifyMfaFactorError.MfaFactorNotAllowedError || error instanceof IamSdkErrors.VerifyUserMfaFactorError.MfaFactorNotAllowedError) {
                            return of(MfaActions.VerifyOtpFailure({ error: error.name }));
                        } else {
                            return of(MfaActions.VerifyOtpFailure({ error: error.message }));
                        }
                    }),
                    catchError((error: any) => of(MfaActions.VerifyOtpFailure({ error: error.name })))
                );
            })
        )
    );

    verifyOtpSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MfaActions.VerifyOtpSuccess),
            switchMap((action) => {
                const { identity, region } = action;
                this.cookiesService.setCookie('identity', identity);
                config.setToken(identity.token.token);
                const userLanguage = identity?.language || this.store$.select(fromRoot.getSelectedLanguage);
                const languageWithFallback = getLanguageWithFallback(userLanguage);
                const discoverySdk = new Discovery({ region: identity.region as Region });
                return defer(() => discoverySdk.listPublicServices()).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: ListPublicServicesError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListPublicServicesError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListPublicServicesError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    concatMap((resServices: any) => {
                        const { region, country, countryCode } = resServices;
                        const isRoot = this.tokenService.isRoot(identity.token.token);
                        const account = this.tokenService.getAccountFromToken(identity.token.token);
                        const computedIdentity = {
                            ...identity,
                            account,
                            isRoot
                        };

                        return [
                            AccountActions.selectActiveIdentity({ identity: computedIdentity }),
                            EnvironmentActions.SelectLanguage({ language: languageWithFallback }),
                            EnvironmentActions.loadServicesSuccess({ region, country, countryCode }),
                            SigninActions.signinCheckAccountType({ identity: computedIdentity, region }),
                        ];
                    }),
                    catchError((error: any) => of(MfaActions.VerifyOtpFailure({ error: error.name })))
                );
            })
        )
    );

    verifyApplicationFactor$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MfaActions.VerifyApplicationFactor),
            withLatestFrom(
                this.store$.select(fromSignin.getMfaToken),
                this.store$.select(fromSignin.getMfaRegion),
                this.store$.select(fromSignin.getMfaApplicationSecret),
            ),
            exhaustMap(([action, token, region, secret]) => {
                const iamSdk = new Iam({ region });
                const { account, id } = this.tokenService.decodeToken(token);
                const isRoot = this.tokenService.isRoot(token);
                const req = isRoot ? iamSdk.verifyMfaFactor({ factorId: 'application', otp: action.otp, token, secret }) : iamSdk.verifyUserMfaFactor({ userId: id, factorId: 'application', otp: action.otp, token, secret });
                return defer(() => req).pipe(
                    exhaustMap(() => {
                        return of(MfaActions.VerifyApplicationFactorSuccess());
                    }),
                    catchError(error => of(MfaActions.VerifyApplicationFactorFailure({ error: error.message })))
                );
            }),
            catchError(error => of(MfaActions.VerifyApplicationFactorFailure({ error: error.message })))
        )
    );

    verifyEmailFactor$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MfaActions.VerifyEmailFactor),
            withLatestFrom(
                this.store$.select(fromSignin.getMfaToken),
                this.store$.select(fromSignin.getMfaRegion),
            ),
            exhaustMap(([action, token, region]) => {
                const iamSdk = new Iam({ region });
                const { id } = this.tokenService.decodeToken(token);
                const isRoot = this.tokenService.isRoot(token);
                const req = isRoot ? iamSdk.verifyMfaFactor({ factorId: 'email', otp: action.otp, token }) : iamSdk.verifyUserMfaFactor({ userId: id, factorId: 'email', otp: action.otp, token });
                return defer(() => req).pipe(
                    exhaustMap(() => {
                        return of(MfaActions.VerifyEmailFactorSuccess());
                    }),
                    catchError(error => of(MfaActions.VerifyEmailFactorFailure({ error: error.message })))
                );
            }),
            catchError(error => of(MfaActions.VerifyEmailFactorFailure({ error: error.message })))
        )
    );

    createMfaFactorSecret$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MfaActions.CreateMfaFactorSecret),
            withLatestFrom(
                this.store$.select(fromSignin.getMfaToken),
                this.store$.select(fromSignin.getMfaRegion),
            ),
            switchMap(([action, token, region]) => {
                const iamSdk = new Iam({ region });
                const { account, id } = this.tokenService.decodeToken(token);
                const isRoot = this.tokenService.isRoot(token);
                const req = isRoot ? iamSdk.createMfaFactorSecret({ factorId: 'application', token }) : iamSdk.createUserMfaFactorSecret({ userId: id, factorId: 'application', token });
                return defer(() => req).pipe(
                    map(({ application }) => MfaActions.CreateMfaFactorSecretSuccess({ secret: application.secret })),
                    catchError(error => of(MfaActions.CreateMfaFactorSecretFailure({ error: error.message })))
                );
            })
        )
    );

    constructor(
        private actions$: Actions,
        private store$: Store<fromSignin.State & fromRoot.State>,
        private cookiesService: CookiesService,
        private tokenService: TokenService,
        private router: Router,
    ) { }

}
